import { ApolloClient, ApolloProvider as ApolloProviderLib, DocumentNode, useApolloClient } from '@apollo/client';
import { ApolloProviderProps } from '@apollo/client/react/context';
import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { isDefined } from '@flame-frontend-utils/commons';
import { useLoginInfo } from '../../react-hooks/useLoginInfo';
import { MediaPostsDocument } from '../../react-hooks/useMediaPosts/gql/MediaPosts.document';
import { Kind } from 'graphql';

interface CurrentUserDataFetcherProps {
  children: ReactNode;
}

/**
 * SSR result is user-agnostic, so we have to refetch everything if we're actually logged in. Otherwise, there is no
 * need for that.
 */
const ApolloProvider = ({ client, children, ...restProps }: ApolloProviderProps<unknown>): JSX.Element => {
  // eslint-disable-next-line no-param-reassign
  client.disableNetworkFetches = true;

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    client.disableNetworkFetches = false;
  }, [client]);

  return (
    <ApolloProviderLib client={client} {...restProps}>
      <CurrentUserDataFetcher>{children}</CurrentUserDataFetcher>
    </ApolloProviderLib>
  );
};

const CurrentUserDataFetcher = ({ children }: CurrentUserDataFetcherProps) => {
  const { data } = useLoginInfo();
  const client = useApolloClient();

  const [userSpecificDataLoaded, setUserSpecificDataLoaded] = useState(false);

  useEffect(() => {
    const effect = async () => {
      if (data) {
        if (data.loginInfo.user) {
          // Prevent feed refetching to avoid UI flickering.
          await refetchQueries({
            client,
            exclude: [MediaPostsDocument],
          });
        }

        setUserSpecificDataLoaded(true);
      }
    };

    void effect();
  }, [client, data]);

  return (
    <UserSpecificDataLoadedContext.Provider value={userSpecificDataLoaded}>
      {children}
    </UserSpecificDataLoadedContext.Provider>
  );
};

interface RefetchObservableQueriesOptions {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  client: ApolloClient<any>;
  exclude?: (string | DocumentNode)[];
}

async function refetchQueries({ client, exclude }: RefetchObservableQueriesOptions) {
  const stringExclude = exclude
    ?.map((item) =>
      typeof item === 'string'
        ? item
        : item.definitions
            .filter((def) => def.kind === Kind.OPERATION_DEFINITION)
            .map((def) => def.name?.value)
            .filter(isDefined),
    )
    .flat();

  const queries = client.getObservableQueries();

  const filteredQueries = Array.from(queries.values()).filter((observableQuery) => {
    const operationNames = observableQuery.query.definitions
      .filter((def) => def.kind === Kind.OPERATION_DEFINITION)
      .map((def) => def.name?.value);

    return !operationNames.some((name) => name && stringExclude?.includes(name));
  });

  return Promise.all(filteredQueries.map((observableQuery) => observableQuery.refetch()));
}

const UserSpecificDataLoadedContext = createContext(true);

const useUserSpecificDataLoaded = () => useContext(UserSpecificDataLoadedContext);

export { ApolloProvider, useUserSpecificDataLoaded };
